import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getRecord,
  getRetention,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  type RecordRow,
  type RetentionState,
} from '../lib/api';
import { RecordMedia, RecordAudio } from '../components/RecordMedia';
import { useRecordDetail } from '../lib/useRecordDetail';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { humanizeValue } from '../lib/fieldDisplay';
import { AA, LINK, otherEnd } from '../lib/auditionSpread';
import { readerOf, reviewHasAudio, runningTime, stamp, timelineMarks } from '../lib/selfTape';
import '../components/RetentionChooser.css';
import './spread.css';
import './selfTape.css';

// A6 — ONE TAPE, AND WHAT CAME BACK.
//
// The evening in the order it happened: where it was shot, how many takes,
// WHICH ONE WENT, what the parent noticed — then whatever a coach or a
// casting office sent back.
//
// ===================================================================
// `take_sent` IS THE LARGEST THING ON THE SCREEN
// ===================================================================
// Same reasoning as `pickup_at` on the shoot-day card: a screen has one job,
// and its one job should be the biggest thing on it. The pack calls this "the
// single most-forgotten fact in a self-tape household, and the one the coach
// asks about first". A parent opening this three weeks later is looking for
// one number.
//
// ===================================================================
// TWO RULES THIS SCREEN CANNOT BREAK
// ===================================================================
// 1. A SIBLING WHO READ OFF CAMERA IS NOT THE SUBJECT OF THE TAPE.
//    `media_subject` is aboutness and is the only media pointer the pack's
//    `relations` declares; `media_reader_subject` is a favour. The reader is
//    rendered as an ACTION ("Breeze read") and never as ownership, because a
//    line that blurs them is one refactor away from putting one child's
//    material in another child's list.
//
// 2. THE TRANSCRIPT NEVER STANDS IN FOR THE AUDIO. The pack retains
//    `audio_ref` AND `transcript` and says why: "a coach's tone carries half
//    the note, and a transcript that silently stood in for the recording
//    would lose it". Where a review has audio, the audio is offered FIRST and
//    the transcript is explicitly labelled as a transcript OF it — never
//    presented as the note itself.

interface Props {
  tenantId: string;
  id: string;
  onBack: () => void;
  onOpenRole: (id: string) => void;
  onOpenAudition: (id: string) => void;
}

interface Around {
  loaded: boolean;
  performer: RecordRow | null;
  role: RecordRow | null;
  audition: RecordRow | null;
  reviews: RecordRow[];
  reviewers: Map<string, string>;
  subjects: Map<string, string>;
  people: Map<string, string>;
}

const EMPTY: Around = {
  loaded: false,
  performer: null,
  role: null,
  audition: null,
  reviews: [],
  reviewers: new Map(),
  subjects: new Map(),
  people: new Map(),
};

/** A record we may not read is a blank here, not an error — but ONLY a
 * refusal is a blank. Anything else rethrows so the whole read reports
 * itself (AuditionSpread's own readOrNull rule). */
async function readOrNull(tenantId: string, id: string | null): Promise<RecordRow | null> {
  if (!id) return null;
  try {
    return await getRecord(tenantId, id);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return null;
    throw err;
  }
}

function at(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  // A date recorded to the day (a headshot's shoot date, a reel's cut date)
  // lands at local midnight; "Thu, Apr 16, 12:00 AM" claims a clock time
  // nobody recorded. Show the day alone in that case.
  if (d.getHours() === 0 && d.getMinutes() === 0) {
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  }
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function TapeRecord({ tenantId, id, onBack, onOpenRole, onOpenAudition }: Props) {
  const { record, links, error, reload } = useRecordDetail(tenantId, AA.MEDIA, id);
  const recordsVersion = useRecordsVersion();
  const [around, setAround] = useState<Around>(EMPTY);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  // F3 — this tape's own retention state, or null for "we have not read it".
  //
  // ⚑ THE MARK IS ALL THAT LIVES HERE. The CHOICE is on the Materials log,
  // where the whole batch is: a per-tape delete button would turn a household
  // decision into forty separate ones and make "clear the space" something a
  // parent does by accident on the way to watching a take. What this screen
  // owes is honesty — that this tape has reached the window and is still,
  // visibly, here.
  const [tapeRetention, setTapeRetention] = useState<RetentionState | null>(null);

  const performerId = otherEnd(links, id, LINK.MEDIA_SUBJECT);
  const roleId = otherEnd(links, id, LINK.MEDIA_ROLE);
  const auditionId = otherEnd(links, id, LINK.MEDIA_AUDITION);

  useEffect(() => {
    let cancelled = false;
    if (!record) return;
    setAround((prev) => ({ ...prev, loaded: false }));
    async function load() {
      try {
        const [performer, role, audition, allReviews, subjects, people] = await Promise.all([
          readOrNull(tenantId, performerId),
          readOrNull(tenantId, roleId),
          readOrNull(tenantId, auditionId),
          listAllRecordsWithLinks(tenantId, AA.REVIEW, {
            linkTypes: [LINK.REVIEW_MEDIA, LINK.REVIEW_REVIEWER],
          }),
          listAllRecordsByType(tenantId, AA.SUBJECT).catch(() => [] as RecordRow[]),
          listAllRecordsByType(tenantId, AA.PERSON).catch(() => [] as RecordRow[]),
        ]);
        if (cancelled) return;

        const mine = activeOnly(allReviews.records).filter(
          (r) => otherEnd(allReviews.linksByRecordId[r.id] ?? [], r.id, LINK.REVIEW_MEDIA) === id
        );
        // Newest first: a note that came back later supersedes an earlier one
        // in the parent's head, so it belongs at the top.
        mine.sort((a, b) => String(b.fields.received_at ?? '').localeCompare(String(a.fields.received_at ?? '')));

        const peopleMap = new Map(people.map((r) => [r.id, r.display_name]));
        const reviewers = new Map<string, string>();
        for (const r of mine) {
          const who = otherEnd(allReviews.linksByRecordId[r.id] ?? [], r.id, LINK.REVIEW_REVIEWER);
          const name = who ? peopleMap.get(who) : undefined;
          if (name) reviewers.set(r.id, name);
        }

        setAround({
          loaded: true,
          performer,
          role,
          audition,
          reviews: mine,
          reviewers,
          subjects: new Map(subjects.map((r) => [r.id, r.display_name])),
          people: peopleMap,
        });
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setAround((prev) => ({ ...prev, loaded: true }));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, record?.id, performerId, roleId, auditionId, recordsVersion]);

  const fileRef = typeof record?.fields.original_file_ref === 'string' ? record.fields.original_file_ref : null;
  useEffect(() => {
    let cancelled = false;
    if (!fileRef) {
      setTapeRetention(null);
      return;
    }
    void getRetention(tenantId)
      .then((r) => {
        if (cancelled) return;
        // The endpoint returns only what is warned or due, so an absent id
        // means "nothing approaching" — and a household on a plan that keeps
        // everything gets an empty list, which is the same absence.
        setTapeRetention(r.files.find((f) => f.fileId === fileRef)?.state ?? null);
      })
      .catch(() => {
        // Absent, never guessed.
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, fileRef, recordsVersion]);

  if (error) {
    return (
      <div class="st">
        <div class="st__eyebrow">Tape</div>
        <div class="sp__say">
          {error.headline}
          <br />
          <span class="sp__say-soft">{error.detail}</span>
        </div>
        {error.retryable ? (
          <button type="button" class="sp__retry" onClick={reload}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }
  if (!record) {
    return (
      <div class="st">
        <div class="st__eyebrow">Tape</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const f = record.fields;
  // The word this screen uses for what it shows follows the media kind
  // (Huan, the demo, 2026-09-27: a headshot read "this tape").
  const kindKey = typeof f.kind === 'string' ? f.kind : '';
  const isStill = kindKey === 'headshot' || kindKey === 'photo';
  const mediaNoun = kindKey === 'headshot' ? 'headshot' : kindKey === 'photo' ? 'photo' : kindKey === 'reel' ? 'reel' : 'tape';
  const reader = readerOf(links, id, { subjects: around.subjects, people: around.people }, {
    subject: LINK.MEDIA_READER_SUBJECT,
    person: LINK.MEDIA_READER,
  });
  const sent = typeof f.take_sent === 'number' ? f.take_sent : null;
  const shot = typeof f.takes_shot === 'number' ? f.takes_shot : null;
  const dur = runningTime(f.duration);
  const corner = typeof f.corner === 'string' && f.corner.trim() ? f.corner.trim() : null;
  const observation = typeof f.observation === 'string' && f.observation.trim() ? f.observation.trim() : null;

  return (
    <div class="st">
      <div class="sp__crumb">
        <button type="button" onClick={onBack}>
          Materials
        </button>
        <span class="sp__crumb-sep">›</span>
        <span class="sp__crumb-cur">this {mediaNoun}</span>
      </div>

      {failure ? <div class="sp__say sp__say-soft">{failure.headline}</div> : null}

      <div class="st__eyebrow">
        {typeof f.kind === 'string' ? humanizeValue(f.kind) : 'Media'}
        {at(f.recorded_at) ? ` · ${at(f.recorded_at)}` : ''}
      </div>
      <h1 class="st__title">
        {typeof f.label === 'string' && f.label.trim() ? f.label : (around.role?.display_name ?? 'This tape')}
      </h1>

      {/* F3 — the mark, and the sentence that makes it safe to read. A parent
          who sees an age on their child's tape and no reassurance beside it
          will assume the worst; the second half of this line is not a caption
          explaining a control, it is the fact itself. */}
      {tapeRetention ? (
        <div class="rt__badge-block">
          <span class={`rt__badge${tapeRetention === 'due' ? ' rt__badge--due' : ''}`}>
            {tapeRetention === 'due' ? 'past the keeping window' : 'nearing the keeping window'}
          </span>
          <span class="rt__badge-say">still here, and yours to keep or clear from Materials</span>
        </div>
      ) : null}

      {/* ===================================================================
          A12 — THE TAPE ITSELF, FIRST. The A6 header's own ruling ("the
          recording is the truth — this is the source of truth") finally has
          the recording on the screen it ruled about. Present only when a file
          is attached: an empty player over a log that says "7 takes shot"
          would read as the tape being lost, and absence-with-no-furniture is
          this product's idiom for "not recorded here".
          `playable` rides the request — an Android tape plays on the iPhone
          via its CV7 rendition, and Download (the editor) still hands over
          the original bytes.

          ⚑ `RecordMedia`, NOT `RecordVideo` — 2026-09-20. This screen opens
          for ANY `aa_media` kind (HouseholdTapes.tsx lists every kind
          together, on purpose — see its own header), so `original_file_ref`
          is not always a tape. It used to be `<RecordVideo>` unconditionally,
          which is exactly how a `kind: 'photo'` row (the demo seed's 9
          portraits/EPK stills) rendered as `<video src="a JPEG">` — a player
          that can never play. `RecordMedia` asks the door what the bytes
          actually are and renders the tag that mime type needs. */}
      {typeof f.original_file_ref === 'string' && f.original_file_ref ? (
        <div class="st__player">
          <RecordMedia tenantId={tenantId} recordId={id} fieldName="original_file_ref" alt="" />
        </div>
      ) : null}

      {/* THE NUMBER. */}
      {sent !== null ? (
        <div class="st__sent-block">
          <div class="st__k">Take sent</div>
          <div class="st__sent-big">
            {sent}
            {shot !== null ? <span class="st__sent-of"> of {shot}</span> : null}
          </div>
        </div>
      ) : shot !== null ? (
        <div class="st__sent-block">
          <div class="st__k">Takes shot</div>
          <div class="st__sent-big st__sent-big--gap">{shot}</div>
          <div class="st__gap-line">No take marked as sent.</div>
        </div>
      ) : null}

      <div class="st__facts">
        {corner ? (
          <div>
            <div class="st__k">Where</div>
            <div class="st__v">{corner}</div>
          </div>
        ) : null}
        {dur ? (
          <div>
            <div class="st__k">Running time</div>
            <div class="st__v">{dur}</div>
          </div>
        ) : null}
        {reader ? (
          <div>
            <div class="st__k">Off camera</div>
            {/* AN ACTION, NEVER OWNERSHIP. "Breeze read" cannot be misread as
                "Breeze's tape"; "Breeze" under a heading like "Performer"
                could be, and that is the leak. */}
            <div class="st__v">{reader.name} read</div>
          </div>
        ) : null}
      </div>

      {observation ? (
        <div class="st__observation">
          <div class="st__k">What you noticed</div>
          <p>{observation}</p>
        </div>
      ) : null}

      {(around.role || around.audition) && (
        <div class="st__links">
          {around.role ? (
            <button type="button" onClick={() => onOpenRole(around.role!.id)}>
              {around.role.display_name}
            </button>
          ) : null}
          {around.audition ? (
            <button type="button" onClick={() => onOpenAudition(around.audition!.id)}>
              the read it went to
            </button>
          ) : null}
        </div>
      )}

      <div class="st__edit">
        <button type="button" class="sp__edit" onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.MEDIA, id })}>
          Edit this {mediaNoun}
        </button>
      </div>

      {/* A review is what comes back on a TAPE. A headshot or a photo opened
          here (this screen serves every aa_media kind — see the RecordMedia
          note above) has nothing to "come back", so the section shows for a
          still only when a review already exists on it (never hides a fact). */}
      {isStill && around.loaded && around.reviews.length === 0 ? null : (
      <section class="st__reviews">
        <h2 class="st__sec">
          <span>What came back</span>
          {/* A12 — a review could only be made through the GLOBAL create,
              linked to nothing, which is the same defect class as the acting
              booking that had nowhere to be recorded (A10's worst find). The
              act lives where the thing it is about lives, pre-pointed. */}
          <button
            type="button"
            class="st__add-review"
            onClick={() =>
              openRecordEditor({
                mode: 'create',
                typeKey: AA.REVIEW,
                prefillRels: { [LINK.REVIEW_MEDIA]: [id] },
              })
            }
          >
            + A note came back
          </button>
        </h2>
        {!around.loaded ? (
          <div class="sp__empty">Reading…</div>
        ) : around.reviews.length === 0 ? (
          <div class="sp__empty">Nothing back on this one yet.</div>
        ) : (
          around.reviews.map((r) => {
            const marks = timelineMarks(r.fields.timeline_marks);
            const hasAudio = reviewHasAudio(r);
            const transcript =
              typeof r.fields.transcript === 'string' && r.fields.transcript.trim()
                ? r.fields.transcript.trim()
                : null;
            const body = typeof r.fields.body === 'string' && r.fields.body.trim() ? r.fields.body.trim() : null;
            return (
              <article key={r.id} class="st__review">
                <div class="st__review-head">
                  <span class="st__review-who">
                    {around.reviewers.get(r.id) ??
                      (typeof r.fields.reviewer_role === 'string' && r.fields.reviewer_role.trim()
                        ? r.fields.reviewer_role
                        : 'Note')}
                    {around.reviewers.get(r.id) &&
                    typeof r.fields.reviewer_role === 'string' &&
                    r.fields.reviewer_role.trim() ? (
                      <span class="st__review-role"> · {r.fields.reviewer_role}</span>
                    ) : null}
                  </span>
                  <span class="st__review-when">{at(r.fields.received_at) ?? ''}</span>
                </div>

                {/* AUDIO FIRST, ALWAYS. */}
                {hasAudio ? (
                  <div class="st__audio">
                    {/* A12: the recording PLAYS here now. It used to open the
                        record editor — a coach's tone reached the parent as a
                        form. The editor stays reachable for editing; hearing
                        the note is not editing. */}
                    <RecordAudio tenantId={tenantId} recordId={r.id} fieldName="audio_ref" />
                  </div>
                ) : null}

                {body ? <p class="st__review-body">{body}</p> : null}

                {transcript ? (
                  <div class="st__transcript">
                    {/* LABELLED AS A TRANSCRIPT OF something, so it can never
                        pass as the note itself. Where there is no audio it is
                        still labelled — the reader deserves to know which of
                        the two they are holding. */}
                    <div class="st__k">{hasAudio ? 'Transcript of the recording' : 'Transcript'}</div>
                    <p>{transcript}</p>
                  </div>
                ) : null}

                {marks.length > 0 ? (
                  <ol class="st__marks">
                    {marks.map((m) => (
                      <li key={`${m.atSeconds}-${m.note}`}>
                        <span class="st__mark-at">{stamp(m.atSeconds)}</span>
                        <span class="st__mark-note">{m.note}</span>
                      </li>
                    ))}
                  </ol>
                ) : null}
              </article>
            );
          })
        )}
      </section>
      )}
    </div>
  );
}
